import { useWindowDimensions, Platform, KeyboardAvoidingView, Dimensions, TextInput, ScrollView, Alert } from 'react-native';
import Modal from 'react-native-modal';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import * as DocumentPicker from 'expo-document-picker';
import apis from '@api/api';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import moment from 'moment';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialIcons } from '@expo/vector-icons';
import { RichEditorProps } from 'react-native-pell-rich-editor';
import EditorToolbar from '@components/Global/EditorToolbar/EditorToolbar';
import TempScreen from '@components/Global/htmlEditor/HtmlEditor';
import { DefaultRootState } from '@reduxLocal/persistState';
import { YStack, XStack, H2, H3, H4, SizableText, Card, Button, Separator } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { FlatList, View } from 'react-native';

type MailModalType = {
  visible: boolean,
  setVisible: Function,
  mailItem?: Object,
  sendTo?: string,
  sendSubject?: string,
  sendFrom?: string,
  setBody?: string
}

const MailModal = ({ visible, setVisible, mailItem, sendTo, sendFrom, sendSubject }: MailModalType) => {
  const {accessToken, email: mailAccount} = googleAuths();
  const { width, height } = useWindowDimensions();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const theme = themeSelector();

  const [toState, setToState] = useState(sendTo ? sendTo : "");
  const [subjectState, setSubjectState] = useState(sendSubject ? sendSubject : "");
  const [fromState, setFromState] = useState(mailAccount || "");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<any[]>([]);
  const dispatch = useDispatch();
  const editorRef = useRef<RichEditorProps>(null);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

  // Update state when props change
  useEffect(() => {
    if (sendTo) setToState(sendTo);
    if (sendSubject) setSubjectState(sendSubject);
    if (sendFrom) setFromState(sendFrom);
  }, [sendTo, sendSubject, sendFrom]);

  const _hideModal = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const _handleSend = async () => {
    try {
      const emailContent = `From: <${fromState}>\nTo: ${toState}\nSubject: ${subjectState}\n\n${body}`;
      await apis.sendGoogleMail(accessToken, { raw: emailContent }).then(res => {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Mail sent', type: 'success' } });
        _hideModal();
      });
    } catch (error) {
      console.error('Error sending mail:', error);
      Alert.alert('Error', 'Failed to send email. Please try again.');
    }
  };

  const _handleAttach = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ multiple: true });
      if (!result.canceled && 'assets' in result && result.assets) {
        setAttachments(prev => [...prev, ...result.assets]);
      }
    } catch (e) {
      console.error('file could not be attached', e);
      Alert.alert('Error', 'Failed to attach file. Please try again.');
    }
  };

  const _handleSaveDraft = () => {
    // TODO: Implement save draft functionality
    dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Draft saved', type: 'success' } });
  };

  const _handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };


  return (
    <Modal
      isVisible={visible}
      onBackdropPress={_hideModal}
      deviceHeight={height}
      deviceWidth={width}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      style={{ margin: 0, justifyContent: 'flex-end', marginTop: 40 }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <YStack
          flex={1}
          backgroundColor={theme ? "#ffffff" : "#0f172a"}
          borderTopLeftRadius={20}
          borderTopRightRadius={20}
          paddingTop="$4"
          paddingHorizontal="$4"
          paddingBottom="$6"
          maxHeight="90%"
        >
          {/* Header */}
          <Animatable.View
            animation="fadeInDown"
            duration={400}
          >
            <XStack alignItems="center" justifyContent="space-between" marginBottom="$4">
              <XStack alignItems="center" gap="$3" flex={1}>
                <View style={{
                  backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 12
                }}>
                  <MaterialIcons
                    name="email"
                    size={24}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <H3
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                    numberOfLines={1}
                  >
                    {subjectState || "New Mail"}
                  </H3>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                    marginTop="$1"
                  >
                    {fromState || "Compose email"}
                  </SizableText>
                </YStack>
              </XStack>
              <Button
                backgroundColor="transparent"
                onPress={_hideModal}
                padding="$2"
                borderRadius={50}
              >
                <MaterialIcons
                  name="close"
                  size={24}
                  color={theme ? "#1e293b" : "#f1f5f9"}
                />
              </Button>
            </XStack>
          </Animatable.View>

          <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 20 }}
          >
            {/* Email Form Fields */}
            <Animatable.View
              animation="fadeInUp"
              delay={100}
              duration={400}
            >
              <Card
                backgroundColor={theme ? "#f8fafc" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$4"
                marginBottom="$4"
              >
                <YStack gap="$4">
                  {/* To Field */}
                  <YStack gap="$2">
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name="person"
                        size={18}
                        color={theme ? "#0ea5e9" : "#38bdf8"}
                      />
                      <SizableText
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        size="$4"
                        fontWeight="600"
                      >
                        To
                      </SizableText>
                    </XStack>
                    <TextInput
                      style={{
                        backgroundColor: theme ? "#ffffff" : "#0f172a",
                        borderColor: theme ? "#cbd5e1" : "#475569",
                        borderWidth: 1,
                        borderRadius: 12,
                        paddingHorizontal: 16,
                        paddingVertical: 12,
                        fontSize: 16,
                        color: theme ? "#1e293b" : "#f1f5f9",
                        minHeight: 48,
                      }}
                      value={toState}
                      onChangeText={setToState}
                      placeholder="Recipient email address"
                      placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                  </YStack>

                  {/* Subject Field */}
                  <YStack gap="$2">
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name="subject"
                        size={18}
                        color={theme ? "#059669" : "#10b981"}
                      />
                      <SizableText
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        size="$4"
                        fontWeight="600"
                      >
                        Subject
                      </SizableText>
                    </XStack>
                    <TextInput
                      style={{
                        backgroundColor: theme ? "#ffffff" : "#0f172a",
                        borderColor: theme ? "#cbd5e1" : "#475569",
                        borderWidth: 1,
                        borderRadius: 12,
                        paddingHorizontal: 16,
                        paddingVertical: 12,
                        fontSize: 16,
                        color: theme ? "#1e293b" : "#f1f5f9",
                        minHeight: 48,
                      }}
                      value={subjectState}
                      onChangeText={setSubjectState}
                      placeholder="Email subject"
                      placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    />
                  </YStack>

                  {/* From Field */}
                  <YStack gap="$2">
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons
                        name="account-circle"
                        size={18}
                        color={theme ? "#7c3aed" : "#a855f7"}
                      />
                      <SizableText
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        size="$4"
                        fontWeight="600"
                      >
                        From
                      </SizableText>
                    </XStack>
                    <TextInput
                      style={{
                        backgroundColor: theme ? "#f1f5f9" : "#334155",
                        borderColor: theme ? "#cbd5e1" : "#475569",
                        borderWidth: 1,
                        borderRadius: 12,
                        paddingHorizontal: 16,
                        paddingVertical: 12,
                        fontSize: 16,
                        color: theme ? "#64748b" : "#94a3b8",
                        minHeight: 48,
                      }}
                      value={fromState}
                      editable={false}
                      placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    />
                  </YStack>
                </YStack>
              </Card>
            </Animatable.View>

            {/* Email Body Editor */}
            <Animatable.View
              animation="fadeInUp"
              delay={200}
              duration={400}
            >
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$4"
                marginBottom="$4"
                minHeight={300}
              >
                <YStack gap="$3" flex={1}>
                  <XStack alignItems="center" gap="$2" marginBottom="$2">
                    <MaterialIcons
                      name="edit"
                      size={18}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                    <SizableText
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      size="$4"
                      fontWeight="600"
                    >
                      Message
                    </SizableText>
                  </XStack>
                  {/* Formatting Toolbar - Above Message */}
                  <View style={{
                    backgroundColor: 'transparent',
                    borderRadius: 8,
                    marginBottom: 8,
                    height: 32,
                    overflow: 'hidden',
                  }}>
                    <EditorToolbar
                      omitVideo
                      ref={editorRef}
                      backgroundColor="transparent"
                      iconSize={18}
                      style={{
                        height: 32,
                        paddingVertical: 2,
                        paddingHorizontal: 2,
                        minHeight: 32,
                        maxHeight: 32,
                      }}
                    />
                  </View>
                  {/* Message Editor */}
                  <View style={{
                    flex: 1,
                    backgroundColor: theme ? "#f8fafc" : "#0f172a",
                    borderRadius: 12,
                    minHeight: 250,
                    borderWidth: 1,
                    borderColor: theme ? "#e2e8f0" : "#334155",
                  }}>
                    <TempScreen
                      knowledge
                      placeHolder="Type your message here..."
                      ref={editorRef}
                      onChange={(html) => setBody(html)}
                    />
                  </View>
                </YStack>
              </Card>
            </Animatable.View>

            {/* Attachments Section */}
            {attachments.length > 0 && (
              <Animatable.View
                animation="fadeInUp"
                delay={300}
                duration={400}
              >
                <Card
                  backgroundColor={theme ? "#f8fafc" : "#1e293b"}
                  borderColor={theme ? "#e2e8f0" : "#334155"}
                  borderRadius={16}
                  padding="$4"
                  marginBottom="$4"
                >
                  <XStack alignItems="center" gap="$2" marginBottom="$3">
                    <MaterialIcons
                      name="attach-file"
                      size={18}
                      color={theme ? "#0ea5e9" : "#38bdf8"}
                    />
                    <SizableText
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      size="$4"
                      fontWeight="600"
                    >
                      Attachments ({attachments.length})
                    </SizableText>
                  </XStack>
                  <FlatList
                    horizontal
                    data={attachments}
                    showsHorizontalScrollIndicator={false}
                    keyExtractor={(item, index) => `attachment-${index}`}
                    renderItem={({ item, index }) => (
                      <Card
                        backgroundColor={theme ? "#ffffff" : "#0f172a"}
                        borderColor={theme ? "#cbd5e1" : "#475569"}
                        borderRadius={12}
                        padding="$3"
                        marginRight="$2"
                        minWidth={120}
                      >
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons
                            name="insert-drive-file"
                            size={20}
                            color={theme ? "#0ea5e9" : "#38bdf8"}
                          />
                          <YStack flex={1} minWidth={80}>
                            <SizableText
                              color={theme ? "#1e293b" : "#f1f5f9"}
                              size="$3"
                              numberOfLines={1}
                            >
                              {item.name || `Attachment ${index + 1}`}
                            </SizableText>
                            {item.size && (
                              <SizableText
                                color={theme ? "#64748b" : "#94a3b8"}
                                size="$2"
                              >
                                {(item.size / 1024).toFixed(1)} KB
                              </SizableText>
                            )}
                          </YStack>
                          <Button
                            backgroundColor="transparent"
                            onPress={() => _handleRemoveAttachment(index)}
                            padding="$1"
                            borderRadius={50}
                          >
                            <MaterialIcons
                              name="close"
                              size={16}
                              color={theme ? "#ef4444" : "#f87171"}
                            />
                          </Button>
                        </XStack>
                      </Card>
                    )}
                  />
                </Card>
              </Animatable.View>
            )}

            {/* Date and Info */}
            <Animatable.View
              animation="fadeInUp"
              delay={400}
              duration={400}
            >
              <Card
                backgroundColor={theme ? "#f8fafc" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$3"
                marginBottom="$4"
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="schedule"
                    size={18}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <SizableText
                    color={theme ? "#64748b" : "#94a3b8"}
                    size="$3"
                  >
                    {moment(Date.now()).format(globalDate)}
                  </SizableText>
                </XStack>
              </Card>
            </Animatable.View>
          </ScrollView>

          {/* Action Buttons */}
          <Animatable.View
            animation="fadeInUp"
            delay={500}
            duration={400}
          >
            <XStack
              gap="$2"
              marginTop="$4"
              flexWrap="wrap"
              justifyContent={isTablet ? "flex-start" : "center"}
            >
              <Button
                backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                color="white"
                flex={isTablet ? 0 : 1}
                minWidth={isTablet ? 100 : undefined}
                onPress={_handleSend}
                borderRadius={12}
                paddingVertical="$3"
                paddingHorizontal="$3"
              >
                <XStack alignItems="center" gap="$1.5" flexWrap="nowrap">
                  <MaterialIcons name="send" size={18} color="white" />
                  <SizableText color="white" fontWeight="600" size="$3" numberOfLines={1}>
                    Send
                  </SizableText>
                </XStack>
              </Button>

              <Button
                backgroundColor={theme ? "#059669" : "#10b981"}
                color="white"
                flex={isTablet ? 0 : 1}
                minWidth={isTablet ? 100 : undefined}
                onPress={_handleAttach}
                borderRadius={12}
                paddingVertical="$3"
                paddingHorizontal="$3"
              >
                <XStack alignItems="center" gap="$1.5" flexWrap="nowrap">
                  <MaterialIcons name="attach-file" size={18} color="white" />
                  <SizableText color="white" fontWeight="600" size="$3" numberOfLines={1}>
                    Attach
                  </SizableText>
                </XStack>
              </Button>

              <Button
                backgroundColor={theme ? "#7c3aed" : "#a855f7"}
                color="white"
                flex={isTablet ? 0 : 1}
                minWidth={isTablet ? 100 : undefined}
                onPress={_handleSaveDraft}
                borderRadius={12}
                paddingVertical="$3"
                paddingHorizontal="$3"
              >
                <XStack alignItems="center" gap="$1.5" flexWrap="nowrap">
                  <MaterialIcons name="save" size={18} color="white" />
                  <SizableText color="white" fontWeight="600" size="$3" numberOfLines={1}>
                    Save Draft
                  </SizableText>
                </XStack>
              </Button>

              <Button
                backgroundColor={theme ? "#ef4444" : "#f87171"}
                color="white"
                flex={isTablet ? 0 : 1}
                minWidth={isTablet ? 100 : undefined}
                onPress={_hideModal}
                borderRadius={12}
                paddingVertical="$3"
                paddingHorizontal="$3"
              >
                <XStack alignItems="center" gap="$1.5" flexWrap="nowrap">
                  <MaterialIcons name="delete" size={18} color="white" />
                  <SizableText color="white" fontWeight="600" size="$3" numberOfLines={1}>
                    Cancel
                  </SizableText>
                </XStack>
              </Button>
            </XStack>
          </Animatable.View>
        </YStack>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default MailModal;